// src/pages/auth/EmailVerificationInterstitialPage.tsx
// Phase 5 (P0): Email verification enforcement interstitial
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from 'firebase/auth';
import { useAuth, useAuthActions } from '@/auth/AuthProvider';
import { Mail, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import BrandWordmark from '@/components/brand/BrandWordmark';
import { hasProClaim } from '@/lib/auth/claimsUtils';

export default function EmailVerificationInterstitialPage() {
  const { user, loading } = useAuth();
  const { signOutUser } = useAuthActions();
  const navigate = useNavigate();
  const [resending, setResending] = useState(false);
  const [checkingVerification, setCheckingVerification] = useState(false);
  const [lastResendTime, setLastResendTime] = useState<number | null>(null);
  const [lastCheckTime, setLastCheckTime] = useState<Date | null>(null);
  const RESEND_COOLDOWN_MS = 60 * 1000; // 1 minute

  // Helper function to determine redirect path based on Pro claims
  const getRedirectPath = async (firebaseUser: User): Promise<string> => {
    try {
      const tokenResult = await firebaseUser.getIdTokenResult();
      const claims = tokenResult.claims;
      
      // Check for Pro access via multiple claim patterns
      const hasPro = hasProClaim(claims);
      
      return hasPro ? '/pro/dashboard' : '/cases';
    } catch (error) {
      console.error('[EmailVerificationInterstitial] Failed to get token claims:', error);
      // Fallback to SRL dashboard if claims check fails
      return '/cases';
    }
  };

  // Redirect if not logged in
  useEffect(() => {
    if (!loading && !user) {
      console.info('[EmailVerificationInterstitial] No user logged in, redirecting to login');
      navigate('/login', { replace: true });
    }
  }, [user, loading, navigate]);

  // Redirect if already verified
  useEffect(() => {
    if (user?.emailVerified) {
      console.info('[EmailVerificationInterstitial] Email already verified, redirecting');
      getRedirectPath(user).then(redirectPath => {
        navigate(redirectPath, { replace: true });
      });
    }
  }, [user, navigate]);

  // Auto-check verification status every 5 seconds
  useEffect(() => {
    if (!user || user.emailVerified) return;

    const interval = setInterval(async () => {
      try {
        await user.reload();
        if (user.emailVerified) {
          console.info('[EmailVerificationInterstitial] Email verified!');
          toast.success('Email verified successfully!');
          const redirectPath = await getRedirectPath(user);
          navigate(redirectPath, { replace: true });
        }
      } catch (error) {
        console.warn('[EmailVerificationInterstitial] Failed to reload user:', error);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [user, navigate]);

  const handleResendEmail = async () => {
    if (!user) return;

    // Check cooldown
    const now = Date.now();
    if (lastResendTime && (now - lastResendTime < RESEND_COOLDOWN_MS)) {
      const remainingSec = Math.ceil((RESEND_COOLDOWN_MS - (now - lastResendTime)) / 1000);
      toast.error(`Please wait ${remainingSec} seconds before resending`);
      return;
    }

    setResending(true);

    try {
      // CRITICAL: Get fresh token WITHOUT force refresh to preserve existing claims
      // Force refresh can trigger claim recalculation which may strip Pro status
      const tokenResult = await user.getIdTokenResult(false);
      const claims = tokenResult.claims;
      
      // Detect Pro status
      const isPro = hasProClaim(claims);
      
      console.info('[EmailVerificationInterstitial] Resending with status:', {
        uid: user.uid,
        email: user.email,
        isPro,
      });
      
      // Use centralized email verification service WITHOUT force refresh
      const { sendVerificationEmail } = await import('@/lib/emailVerification');
      await sendVerificationEmail({ 
        user, 
        isPro,
        forceClaimRefresh: false // CRITICAL: Don't refresh claims on resend
      });
      
      setLastResendTime(now);
      toast.success('Verification email sent! Check your inbox.');
    } catch (error: any) {
      console.error('[EmailVerificationInterstitial] Resend error:', error);
      
      if (error.code === 'auth/too-many-requests') {
        toast.error('Too many requests. Please try again later.');
      } else if (error.message?.includes('already verified')) {
        // User might have verified in another tab
        toast.success('Email already verified! Checking status...');
        await user.reload();
        if (user.emailVerified) {
          const redirectPath = await getRedirectPath(user);
          navigate(redirectPath, { replace: true });
        }
      } else {
        toast.error('Failed to send email. Please try again.');
      }
    } finally {
      setResending(false);
    }
  };

  const handleCheckVerification = async () => {
    if (!user) return;

    setCheckingVerification(true);

    try {
      console.info('[EmailVerificationInterstitial] Checking verification status...');
      
      // CRITICAL: Reload user state FIRST to get latest emailVerified from Firebase
      // This fetches the actual verification status from the server
      await user.reload();
      
      const checkTime = new Date();
      setLastCheckTime(checkTime);
      
      console.info('[EmailVerificationInterstitial] User reloaded:', {
        uid: user.uid,
        email: user.email,
        emailVerified: user.emailVerified,
        timestamp: checkTime.toISOString()
      });
      
      if (user.emailVerified) {
        // Success! Email is now verified
        // Force token refresh to ensure claims are updated
        await user.getIdToken(true);
        console.info('[EmailVerificationInterstitial] ✅ Email verified!');
        
        toast.success('Email verified successfully!');
        const redirectPath = await getRedirectPath(user);
        console.info('[EmailVerificationInterstitial] Redirecting to:', redirectPath);
        navigate(redirectPath, { replace: true });
      } else {
        console.warn('[EmailVerificationInterstitial] Email not verified yet');
        toast.error('Not verified yet. If you just clicked the link, wait 10-20 seconds and try again.');
      }
    } catch (error) {
      console.error('[EmailVerificationInterstitial] Check verification error:', error);
      toast.error('Failed to check verification status. Please try again.');
    } finally {
      setCheckingVerification(false);
    }
  };

  const handleUseDifferentEmail = async () => {
    try {
      await signOutUser();
      toast.success('Signed out. You can now sign in with a different account.');
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('[EmailVerificationInterstitial] Sign out error:', error);
      toast.error('Failed to sign out');
    }
  };

  if (loading || !user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-transparent">
        <div className="text-center">
          <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-b-2 border-brand-orange"></div>
          <p className="text-white">Loading...</p>
        </div>
      </div>
    );
  }

  if (user.emailVerified) {
    return null; // Will redirect via useEffect
  }

  const canResend = !lastResendTime || (Date.now() - lastResendTime >= RESEND_COOLDOWN_MS);

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-transparent px-4">
      <div className="relative w-full max-w-md rounded-2xl border border-brand-orange/50 bg-slate-900/90 p-8 backdrop-blur-sm shadow-2xl">
        {/* Logo */}
        <div className="mb-8 flex justify-center">
          <BrandWordmark darkText={false} className="text-3xl" />
        </div>

        {/* Icon */}
        <div className="mb-6 flex justify-center">
          <div className="rounded-full bg-brand-orange/20 p-4">
            <Mail className="h-12 w-12 text-brand-orange" />
          </div>
        </div>

        {/* Title */}
        <h1 className="mb-4 text-center text-2xl font-semibold text-white">
          Verify Your Email
        </h1>

        {/* Message */}
        <p className="mb-6 text-center text-slate-300">
          We sent a verification email to <strong className="text-white">{user.email}</strong>.
          Click the link in the email to verify your account.
        </p>

        {/* Instructions */}
        <div className="mb-6 rounded-lg border border-slate-700 bg-slate-800/50 p-4">
          <div className="mb-3 flex items-start gap-3">
            <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-400" />
            <div className="text-sm text-slate-300">
              <strong className="text-white">Step 1:</strong> Check your inbox (and spam folder)
            </div>
          </div>
          <div className="mb-3 flex items-start gap-3">
            <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-400" />
            <div className="text-sm text-slate-300">
              <strong className="text-white">Step 2:</strong> Click the verification link
            </div>
          </div>
          <div className="flex items-start gap-3">
            <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-400" />
            <div className="text-sm text-slate-300">
              <strong className="text-white">Step 3:</strong> Return here and click "Refresh verification status"
            </div>
          </div>
        </div>

        {/* Last Check Timestamp */}
        {lastCheckTime && (
          <div className="mb-4 text-center text-sm text-slate-400">
            Last checked: {lastCheckTime.toLocaleTimeString()}
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={handleCheckVerification}
            disabled={checkingVerification}
            className="w-full rounded-lg bg-brand-orange px-5 py-3 font-bold text-white shadow-md transition-all hover:-translate-y-0.5 hover:bg-orange-600 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
          >
            {checkingVerification ? (
              <span className="flex items-center justify-center gap-2">
                <RefreshCw className="h-5 w-5 animate-spin" />
                Checking...
              </span>
            ) : (
              "Refresh Verification Status"
            )}
          </button>

          <button
            onClick={handleResendEmail}
            disabled={resending || !canResend}
            className="w-full rounded-lg border border-slate-700 bg-slate-800 px-5 py-3 font-medium text-slate-300 transition-all hover:bg-slate-700 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {resending ? (
              <span className="flex items-center justify-center gap-2">
                <RefreshCw className="h-5 w-5 animate-spin" />
                Sending...
              </span>
            ) : (
              "Resend Verification Email"
            )}
          </button>

          <button
            onClick={handleUseDifferentEmail}
            className="w-full rounded-lg border border-slate-600 bg-transparent px-5 py-2 text-sm font-medium text-slate-400 transition-all hover:border-slate-500 hover:text-slate-300"
          >
            Sign in with Different Account
          </button>
        </div>

        {/* Warning */}
        {!canResend && (
          <div className="mt-4 rounded-lg border border-yellow-600/30 bg-yellow-900/20 p-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-400" />
              <p className="text-sm text-yellow-200">
                Please wait 60 seconds between resend requests
              </p>
            </div>
          </div>
        )}

        {/* Help Text */}
        <div className="mt-6 border-t border-slate-700 pt-6">
          <p className="text-center text-sm text-slate-400">
            Didn't receive the email? Check your spam folder or try resending.
          </p>
          <p className="mt-2 text-center text-sm text-slate-500">
            Need to use a different account? Click "Sign in with Different Account" above.
          </p>
        </div>
      </div>
    </div>
  );
}
